import React, { useRef, useContext, createContext, useState } from "react";
import type { FC, PropsWithChildren } from "react";

import { useFocus } from "../../../../hooks/useFocus";
import { getMask } from "../../../../utils/ui/inputs/masks";
import { useInput } from "../hooks/useInput";

import type { AvailableMasksTypeKeys } from "../../../../utils/ui/inputs/masks";
import type { ICoreInputTextSimpleProps } from "../types";

interface IUseStoreOptions
    extends Pick<ICoreInputTextSimpleProps, "value" | "onChange"> {
    mask?: AvailableMasksTypeKeys;
}

const useStore = (options: IUseStoreOptions) => {
    /** Ссылка на инпут (CoreInputText.Input) */
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    /** Показываем/скрываем пароль */
    const [isHiddenPass, setIsHiddenPass] = useState(
        inputRef?.current?.type === "password",
    );

    const { onClear, onShowHide, value, onChange } = useInput({
        ref: inputRef,
        value: options.value,
        onChange: options.onChange,
    });

    const { isFocused } = useFocus(inputRef, false, false);

    const mask = getMask(options.mask);

    const showHideHandler = (
        e: React.MouseEvent,
        onClick?: (e: React.MouseEvent, isVisible?: boolean) => void,
    ) => {
        onShowHide?.();
        setIsHiddenPass((prev) => {
            onClick?.(e, !prev);
            return !prev;
        });
    };

    return {
        inputRef,
        onClear,
        onShowHide: showHideHandler,
        value,
        onChange,
        isFocused,
        mask,
        isHiddenPass,
    };
};

const Context = createContext({} as ReturnType<typeof useStore>);

interface IInputProvider extends PropsWithChildren, IUseStoreOptions {}

export const InputProvider: FC<IInputProvider> = ({ children, ...options }) => {
    const value = useStore(options);
    // eslint-disable-next-line ok-custom-rules/check-attribute
    return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useInputStore = () => useContext(Context);
